import React, { useState } from 'react';
import { useAppSelector, useAppDispatch } from 'src/store';
import { updateChannel } from 'src/features/account/channels.slice';
import FollowUpSettingsForm from '../FollowUpSettingsForm';
import { FollowUpSettings } from 'src/types/followUpSettings';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, CircularProgress, Box, Tooltip, alpha, IconButton } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import ChannelsService from 'src/services/channels.services';
import theme from 'src/themes/oppy-default-theme';

interface ChannelFollowUpSettingsProps {
  channelId: string;
}

const DEFAULT_PROMPT = "Monitor this conversation and determine if a follow-up is needed. Consider: user engagement, unanswered questions, and potential next steps. If appropriate, draft and send a follow-up message.";

const ChannelFollowUpSettings: React.FC<ChannelFollowUpSettingsProps> = ({ channelId }) => {
  const dispatch = useAppDispatch();
  const channel = useAppSelector(state => state.channels.entities[channelId]);
  const [followUpSettings, setFollowUpSettings] = useState<FollowUpSettings>({
    enabled: channel?.follow_up?.enabled ?? false,
    delay_minutes: channel?.follow_up?.delay_minutes ?? 1440, // Default to Daily
    prompt: channel?.follow_up?.prompt ?? DEFAULT_PROMPT,
    respect_business_hours: channel?.follow_up?.respect_business_hours ?? false,
  });
  const [saving, setSaving] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [initialFollowUpSettings, setInitialFollowUpSettings] = useState<FollowUpSettings>(followUpSettings);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [isConfirmingBulkDelete, setIsConfirmingBulkDelete] = useState(false);
  const [isConfirmingBulkEnable, setIsConfirmingBulkEnable] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkEnabling, setBulkEnabling] = useState(false);

  // Initialize follow-up settings from channel
  React.useEffect(() => {
    if (channel && channel.follow_up) {
      const followUp = channel.follow_up || {};
      setFollowUpSettings({
        enabled: followUp.enabled ?? false,
        delay_minutes: followUp.delay_minutes ?? 1440,
        prompt: followUp.prompt ?? DEFAULT_PROMPT,
        respect_business_hours: followUp.respect_business_hours ?? false,
        max_message_attempts: followUp.max_message_attempts ?? 5,
        schedule_type: followUp.schedule_type ?? 'auto',
        skip_weekends: followUp.skip_weekends ?? false,
        limit_follow_up_attempts: followUp.limit_follow_up_attempts ?? false
      });
      setInitialFollowUpSettings({
        enabled: followUp.enabled ?? false,
        delay_minutes: followUp.delay_minutes ?? 1440,
        prompt: followUp.prompt ?? DEFAULT_PROMPT,
        respect_business_hours: followUp.respect_business_hours ?? false,
        max_message_attempts: followUp.max_message_attempts ?? 5,
        schedule_type: followUp.schedule_type ?? 'auto',
        skip_weekends: followUp.skip_weekends ?? false,
        limit_follow_up_attempts: followUp.limit_follow_up_attempts ?? false
      });
    }
  }, [channel]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await dispatch(updateChannel({ channelData: { id: channel.id, follow_up: followUpSettings } })).unwrap();
      setIsExpanded(true);
      setInitialFollowUpSettings(followUpSettings);
    } catch (error) {
      console.error('Failed to update follow-up settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleBulkDeleteClick = () => {
    setIsConfirmingBulkDelete(true);
  };

  const handleBulkDeleteConfirmClick = async () => {
    setBulkDeleting(true);
    try {
      const response = await ChannelsService.bulkDeleteChannelFollowUp(channelId);
      enqueueSnackbar(response.data.message || 'Follow-up settings pushed to conversations', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to bulk update follow-up settings:', error);
    } finally {
      setBulkDeleting(false);
      setIsConfirmingBulkDelete(false);
    }
  };

  const handleBulkEnableClick = () => {
    setIsConfirmingBulkEnable(true);
  };

  const handleBulkEnableConfirmClick = async () => {
    setBulkEnabling(true);
    try {
      const response = await ChannelsService.bulkEnableChannelFollowUp(channelId);
      enqueueSnackbar(response.data.message || 'Follow-up settings enabled for conversations', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to bulk enable follow-up settings:', error);
    } finally {
      setBulkEnabling(false);
      setIsConfirmingBulkEnable(false);
    }
  };

  const handleBulkUpdateClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmBulkUpdate = async () => {
    setBulkUpdating(true);
    try {
      const response = await ChannelsService.bulkUpdateChannelFollowUp(channelId);
      enqueueSnackbar(response.data.message || 'Follow-up settings pushed to conversations', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to bulk update follow-up settings:', error);
      const errorMessage = error?.response?.data?.error || error.message || 'Failed to bulk update follow-up settings';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setBulkUpdating(false);
      setConfirmDialogOpen(false);
    }
  };

  return (
    <>
      <FollowUpSettingsForm
        followUpSettings={followUpSettings}
        setFollowUpSettings={setFollowUpSettings}
        handleSave={handleSave}
        saving={saving}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        initialFollowUpSettings={initialFollowUpSettings}
      />

      <Box mt={3}>
        {/* header for existing follow up settings and subheading for how it only applies to currently active conversations */}
        <Typography variant="h6">Existing Follow Up Settings</Typography>
        <Typography variant="body2">This follow up configuration only applies to currently active conversations.</Typography>
        <Tooltip title="Copy the current follow-up configuration to every conversation in this channel">
          <Button
            variant="contained"
            color="warning"
            size="large"
            fullWidth
            sx={{
              fontWeight: 'bold',
              boxShadow: 3,
              textTransform: 'none'
            }}
            onClick={handleBulkUpdateClick}
          >
            Push Follow-up Settings to All Conversations
          </Button>
        </Tooltip>
        {/* bulk delete all scheduled follow ups until future engagement */}
        {isConfirmingBulkDelete ? (
          <Button
            fullWidth
            variant="contained"
            color="error"
            size="large"
            sx={{ mt: 1, fontWeight: 'bold', boxShadow: 3, textTransform: 'none' }}
            startIcon={<DeleteIcon />}
            onClick={handleBulkDeleteConfirmClick}
            disabled={bulkDeleting}
          >
            {bulkDeleting ? 'Disabling…' : 'Confirm Disable'}
          </Button>
        ) : (
          <Tooltip title="Deletes ALL scheduled follow-ups on ALL active conversations and disables follow-up for this channel">
            <Button
              variant="contained"
              color="error"
              size="large"
              fullWidth
              sx={{ mt: 1, fontWeight: 'bold', boxShadow: 3, textTransform: 'none' }}
              startIcon={<DeleteIcon />}
              onClick={handleBulkDeleteClick}
            >
              Bulk Disable Follow-Up
            </Button>
          </Tooltip>
        )}
        {/* bulk enable follow up */}
        {isConfirmingBulkEnable ? (
          <Button
            fullWidth
            variant="contained"
            color="success"
            size="large"
            sx={{ mt: 1, fontWeight: 'bold', boxShadow: 3, textTransform: 'none' }}
            startIcon={<CheckCircleIcon />}
            onClick={handleBulkEnableConfirmClick}
            disabled={bulkEnabling}
          >
            {bulkEnabling ? 'Enabling…' : 'Confirm Enable'}
          </Button>
        ) : (
          <Tooltip title="Enables follow-up for this channel and schedules follow-up messages on ALL active conversations based on these settings">
            <Button
              variant="contained"
              color="success"
              size="large"
              fullWidth
              sx={{ mt: 1, fontWeight: 'bold', boxShadow: 3, textTransform: 'none' }}
              startIcon={<CheckCircleIcon />}
              onClick={handleBulkEnableClick}
            >
              Bulk Enable Follow-Up
            </Button>
          </Tooltip>
        )}
      </Box>

      <Dialog open={confirmDialogOpen} onClose={() => !bulkUpdating && setConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Bulk Follow-up Update</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            This action will overwrite the follow-up settings for <strong>all</strong> conversations in this channel.
            Any follow-ups that are already scheduled will still be sent as planned. The new settings will take effect
            after any existing scheduled follow-ups.
          </Typography>
          <Typography>
            Are you sure you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={bulkUpdating}>Cancel</Button>
          <Button
            onClick={handleConfirmBulkUpdate}
            color="primary"
            variant="contained"
            disabled={bulkUpdating}
            startIcon={bulkUpdating ? <CircularProgress size={20} /> : undefined}
          >
            {bulkUpdating ? 'Updating...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChannelFollowUpSettings; 
